import { useEffect, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Stack,
    Avatar,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    keyframes
} from "@mui/material";

import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";

import { useNavigate } from "react-router-dom";
import API from "../api/api";

const pulse = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(106, 158, 58, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(106, 158, 58, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(106, 158, 58, 0); }
`;

// Bouncing/driving animation for the scooter
const drive = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-2px) rotate(-3deg); }
  75% { transform: translateY(1px) rotate(2deg); }
`;

export default function BottomScrollableBar() {
    const navigate = useNavigate();
    const [cart, setCart] = useState([]);
    const [liveOrders, setLiveOrders] = useState(() => {
        const cached = localStorage.getItem("cachedLiveOrders");
        return cached ? JSON.parse(cached) : [];
    });

    const [openDiscard, setOpenDiscard] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        if (cart.length > 0 && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
            setActiveIndex(0);
        }
    }, [cart]);

    const handleScroll = (e) => {
        const scrollLeft = e.target.scrollLeft;
        const itemWidth = window.innerWidth - 32;
        const newIndex = Math.round(scrollLeft / itemWidth);
        setActiveIndex(newIndex);
    };

    const totalSwipeItems = (cart.length > 0 ? 1 : 0) + liveOrders.length;

    const loadCart = async () => {
        const saved = localStorage.getItem("mealnoCart");
        if (!saved) {
            setCart([]);
            return;
        }
        const cartItems = JSON.parse(saved);
        try {
            const res = await API.post("/validate-cart", {
                items: cartItems.map(item => ({ menu_item_id: item.id, quantity: item.quantity }))
            });
            const validIds = res.data.valid_items.map(item => item.menu_item_id);
            const filteredCart = cartItems.filter(item => validIds.includes(item.id));

            if (filteredCart.length === 0) {
                localStorage.removeItem("mealnoCart");
                setCart([]);
                window.dispatchEvent(new Event("storage"));
                return;
            }
            localStorage.setItem("mealnoCart", JSON.stringify(filteredCart));
            setCart(filteredCart);
        } catch (err) {
            console.log("Cart validation failed");
            setCart(cartItems);
        }
    };

    const loadLiveOrders = async () => {
        const orderIds = JSON.parse(localStorage.getItem("activeOrders")) || [];
        if (orderIds.length === 0) {
            setLiveOrders([]);
            localStorage.removeItem("cachedLiveOrders");
            return;
        }
        try {
            const results = await Promise.all(orderIds.map(id => API.get(`/order/live/${id}`)));
            const orders = results.map(r => r.data);
            
            // Sort by order_id descending so the newest order is ALWAYS first
            const activeOnly = orders
                .filter(order => !["DELIVERED", "REJECTED", "CANCELLED"].includes(order.status))
                .sort((a, b) => b.order_id - a.order_id);
            
            localStorage.setItem("activeOrders", JSON.stringify(activeOnly.map(o => o.order_id)));
            setLiveOrders(activeOnly);
            localStorage.setItem("cachedLiveOrders", JSON.stringify(activeOnly));
        } catch (err) {
            console.log("Live orders fetch failed");
        }
    };

    useEffect(() => {
        loadCart();
        loadLiveOrders();
        const handleStorage = async () => {
            await loadCart();
            await loadLiveOrders();
        };
        window.addEventListener("storage", handleStorage);
        const orderInterval = setInterval(loadLiveOrders, 15000);
        return () => {
            window.removeEventListener("storage", handleStorage);
            clearInterval(orderInterval);
        };
    }, []);

    const discardCart = () => {
        localStorage.removeItem("mealnoCart");
        setCart([]);
        setOpenDiscard(false);
        window.dispatchEvent(new Event("storage"));
    };

    if (cart.length === 0 && liveOrders.length === 0) return null;

    const latestCartItem = cart[cart.length - 1];
    const otherItemsCount = cart.length - 1;

    return (
        <>
            <Box sx={{ position: "fixed", bottom: 85, left: 0, right: 0, zIndex: 1150 }}>
                <Box
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        px: 2, pb: 0.5, overflowX: "auto", whiteSpace: "nowrap",
                        scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch",
                        scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" }
                    }}
                >
                    <Stack direction="row" spacing={2} sx={{ width: "max-content" }}>
                        {/* CART CARD */}
                        {cart.length > 0 && (
                            <Paper
                                elevation={0}
                                onClick={() => navigate("/cart")}
                                sx={{
                                    width: "320px", flexShrink: 0, scrollSnapAlign: "center",
                                    py: 1.2, px: 2, borderRadius: "24px", cursor: "pointer",
                                    bgcolor: "#FFFDF8", color: "#2F2A25", 
                                    border: "1px solid #ECE5DA",
                                    boxShadow: "0 8px 24px rgba(58, 44, 35, 0.08)",
                                    transition: "transform 0.2s ease",
                                    "&:active": { transform: "scale(0.98)" }
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ minHeight: 44 }}>
                                    <Avatar
                                        sx={{
                                            width: 40, height: 40, minWidth: 40, minHeight: 40, flexShrink: 0,
                                            bgcolor: "#6E655E", color: "#FFFFFF"
                                        }}
                                    >
                                        <ShoppingBagIcon fontSize="small" />
                                    </Avatar>

                                    <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <Typography variant="subtitle2" fontWeight="800" sx={{ fontSize: "1rem", lineHeight: 1.1, color: '#3A2C23' }}>
                                                View Cart
                                            </Typography>
                                            <ArrowForwardIosIcon sx={{ fontSize: 12, color: "#6E655E", ml: 0.5 }} />
                                        </Stack>
                                        <Typography variant="caption" noWrap sx={{ display: "block", color: "#6E655E", fontWeight: 600, lineHeight: 1.1, mt: 0.4 }}>
                                            {latestCartItem?.name} {otherItemsCount > 0 && `& ${otherItemsCount} more`}
                                        </Typography>
                                    </Box>

                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); setOpenDiscard(true); }}
                                        sx={{ bgcolor: "rgba(58, 44, 35, 0.05)", "&:hover": { bgcolor: "rgba(58, 44, 35, 0.1)" }, p: 0.6 }}
                                    >
                                        <CloseIcon sx={{ fontSize: 16, color: "#3A2C23" }} />
                                    </IconButton>
                                </Stack>
                            </Paper>
                        )}

                        {/* LIVE ORDERS CARD */}
                        {liveOrders.map(order => (
                            <Paper
                                key={order.order_id}
                                elevation={0}
                                onClick={() => navigate(`/live-order/${order.order_id}`)}
                                sx={{
                                    width: "320px", flexShrink: 0, scrollSnapAlign: "center",
                                    py: 1.2, px: 2, borderRadius: "24px", cursor: "pointer",
                                    bgcolor: "#FFFDF8", border: "1px solid #ECE5DA",
                                    boxShadow: "0 8px 24px rgba(58, 44, 35, 0.08)",
                                    transition: "transform 0.2s ease",
                                    "&:active": { transform: "scale(0.98)" }
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar
                                        sx={{
                                            width: 40, height: 40, minWidth: 40, minHeight: 40, flexShrink: 0,
                                            bgcolor: "#F8C332", color: "#3A2C23"
                                        }}
                                    >
                                        <DeliveryDiningIcon fontSize="small" sx={{ animation: `${drive} 1s infinite linear` }} />
                                    </Avatar>

                                    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
    
    {/* Left Side: Track Order & Dot */}
    <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" fontWeight="800" sx={{ color: "#3A2C23", fontSize: "1rem" }}>
            Track Order
        </Typography>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#6A9E3A", animation: `${pulse} 2s infinite` }} />
    </Stack>

    {/* Right Side: Order Status */}
    <Typography variant="caption" sx={{ color: "#6E655E", fontWeight: 700, textTransform: "capitalize", fontSize: "0.8rem" }}>
        {order.status?.replaceAll("_", " ")?.toLowerCase()}
    </Typography>

</Box>

                                    <ArrowForwardIosIcon sx={{ fontSize: 14, color: "#6E655E", ml: 0.5 }} />
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                </Box>

                <Box sx={{ height: 16, mb: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {totalSwipeItems > 1 && (
                        <Stack direction="row" spacing={1} sx={{ pb: 0, mb: 0 }}>
                            {Array.from({ length: totalSwipeItems }).map((_, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        width: activeIndex === idx ? 12 : 8,
                                        height: 6, borderRadius: 3,
                                        bgcolor: activeIndex === idx ? "#3A2C23" : "rgba(58, 44, 35, 0.2)",
                                        transition: "all 0.3s ease"
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                </Box>
            </Box>

            {/* DISCARD DIALOG */}
            <Dialog
                open={openDiscard}
                onClose={() => setOpenDiscard(false)}
                PaperProps={{
                    sx: { borderRadius: "24px", p: 1, width: "100%", maxWidth: 340, textAlign: "center", bgcolor: '#FFFFFF' }
                }}
            >
                <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: "#F2EEE8", color: "#6E655E", margin: "0 auto", mb: 2 }}>
                        <ShoppingBagIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight="800" sx={{ mb: 1, fontSize: "1.2rem", color: "#3A2C23" }}>
                        Discard Cart?
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6E655E", lineHeight: 1.5 }}>
                        Are you sure you want to clear your cart? Your added items will be lost.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, flexDirection: "column", gap: 1 }}>
                    <Button
                        fullWidth variant="contained" disableElevation onClick={discardCart}
                        sx={{
                            borderRadius: "16px", py: 1.5, bgcolor: "#a27161", color: "#FFFFFF",
                            fontWeight: "800", fontSize: "1rem", textTransform: "none",
                            "&:hover": { bgcolor: "#D24F33" }
                        }}
                    >
                        Yes, Discard
                    </Button>
                    <Button
                        fullWidth disableElevation onClick={() => setOpenDiscard(false)}
                        sx={{
                            borderRadius: "16px", py: 1.5, color: "#6E655E", bgcolor: "#ede4cc",
                            fontWeight: "700", textTransform: "none", ml: "0 !important"
                        }}
                    >
                        Keep Items
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}